/**
 * Public trainee register page — no auth needed. Trainee scans QR, sees today's
 * attendees, picks their own name, then confirms with a password (existing or
 * new). On success, they're logged in and redirected to the dashboard.
 *
 * The URL that the QR encodes is:
 *   /training/register/:courseId?token=<qr_token>
 * Same route handles both the "list of trainees" view and the "confirm my
 * attendance" view — driven by ?reg=<registration_id>.
 */
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { GraduationCap, Search, CheckCircle2, Loader2, ArrowLeft } from 'lucide-react';
import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { paletteForCourse, BRAND } from '@/pages/trainingBrand';
import { formatApiError } from '@/utils/apiError';

const SECTION_LABEL = { probound: 'ProBound', proepoxy: 'ProEpoxy', prodecor: 'ProDecor' };

export default function TrainingRegister() {
  const { courseId } = useParams();
  const [sp] = useSearchParams();
  const token = sp.get('token') || '';
  const activeReg = sp.get('reg');

  return activeReg
    ? <ConfirmView courseId={courseId} token={token} registrationId={activeReg} />
    : <ListView courseId={courseId} token={token} />;
}


// ─── Register list view (default) ────────────────────────────────────────────
function ListView({ courseId, token }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/training-register/${courseId}?token=${encodeURIComponent(token)}`);
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(formatApiError(err) || 'Register unavailable');
        }
        if (!cancelled) setData(await res.json());
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [courseId, token]);

  const filtered = useMemo(() => {
    if (!data) return [];
    const query = q.trim().toLowerCase();
    if (!query) return data.registrations;
    return data.registrations.filter(r => r.name.toLowerCase().includes(query));
  }, [data, q]);

  if (loading) {
    return <FullBleed palette={BRAND.green}><Loader2 className="w-8 h-8 text-white animate-spin" /></FullBleed>;
  }
  if (error) {
    return (
      <FullBleed palette={BRAND.green}>
        <Card className="p-8 max-w-md w-full text-center">
          <div className="w-14 h-14 rounded-full bg-rose-100 text-rose-600 mx-auto flex items-center justify-center mb-3">
            <GraduationCap className="w-6 h-6" />
          </div>
          <h2 className="font-bold text-lg text-gray-900 mb-1">Register unavailable</h2>
          <p className="text-sm text-gray-500 mb-4">{error}. Ask an Ox Resin trainer to check the QR code.</p>
        </Card>
      </FullBleed>
    );
  }

  const palette = paletteForCourse(data.course);

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(180deg, ${palette.primary} 0%, ${palette.dark} 100%)` }}>
      <div className="max-w-2xl mx-auto px-4 pt-8 pb-6">
        <div className="text-center mb-6" style={{ color: palette.ink }}>
          <div className="w-14 h-14 mx-auto rounded-xl bg-white/15 flex items-center justify-center mb-3">
            <GraduationCap className="w-7 h-7" style={{ color: palette.ink }} />
          </div>
          <h1 className="text-2xl font-bold">{data.course.name}</h1>
          <p className="text-sm opacity-80 mt-1">
            {data.course.date}
            {data.course.reference ? ` · ${data.course.reference}` : ''}
            {' · Training register'}
          </p>
        </div>

        <Card className="p-4 mb-3 !bg-white">
          <Label htmlFor="tr-search" className="text-xs uppercase tracking-wider text-gray-500">Find your name</Label>
          <div className="relative mt-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              id="tr-search"
              data-testid="training-register-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Start typing…"
              className="pl-9 bg-white"
              autoComplete="off"
              autoFocus
            />
          </div>
        </Card>

        <div className="space-y-2">
          {filtered.length === 0 ? (
            <Card className="p-6 text-center text-sm text-gray-500 !bg-white" data-testid="training-register-empty">
              No matches. If your name isn&apos;t on the register, please speak to an Ox Resin trainer.
            </Card>
          ) : (
            filtered.map(r => (
              <Card
                key={r.id}
                className={`p-4 flex items-center gap-3 ${r.confirmed ? 'bg-emerald-50/95 border-emerald-200' : '!bg-white'}`}
                data-testid={`training-register-row-${r.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-gray-900 truncate">{r.name}</div>
                  <div className="text-xs text-gray-500 truncate">{r.email_masked}</div>
                </div>
                {r.confirmed ? (
                  <div className="inline-flex items-center gap-1 text-emerald-700 text-sm font-medium">
                    <CheckCircle2 className="w-4 h-4" /> Confirmed
                  </div>
                ) : (
                  <Button
                    className="font-bold hover:opacity-90"
                    style={{ background: palette.primary, color: palette.ink }}
                    onClick={() => navigate(`/training/register/${courseId}?token=${encodeURIComponent(token)}&reg=${r.id}`)}
                    data-testid={`training-register-confirm-${r.id}`}
                  >
                    Confirm Attendance
                  </Button>
                )}
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}


// ─── Confirm-attendance view (after tapping a name) ──────────────────────────
function ConfirmView({ courseId, token, registrationId }) {
  const navigate = useNavigate();
  const { checkAuth } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lookup, setLookup] = useState(null);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${API_URL}/api/training-register/${courseId}/lookup?registration_id=${encodeURIComponent(registrationId)}&token=${encodeURIComponent(token)}`
        );
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(formatApiError(err) || 'Registration not found');
        }
        if (!cancelled) setLookup(await res.json());
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [courseId, token, registrationId]);

  const submit = async () => {
    if (password.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return;
    }
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/training-register/${courseId}/confirm`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ registration_id: registrationId, token, password }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Confirmation failed');
      }
      const result = await res.json();
      toast.success(result.created_account
        ? 'Welcome to Ox Resin — you\u2019re all set!'
        : 'Attendance confirmed. Welcome back!');
      if (checkAuth) await checkAuth();
      navigate('/', { replace: true });
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <FullBleed palette={BRAND.green}><Loader2 className="w-8 h-8 text-white animate-spin" /></FullBleed>;
  }
  if (error) {
    return (
      <FullBleed palette={BRAND.green}>
        <Card className="p-8 max-w-md w-full text-center !bg-white">
          <h2 className="font-bold text-lg text-gray-900 mb-1">Sorry — {error}</h2>
          <p className="text-sm text-gray-500 mb-4">Go back and re-scan the QR code, or ask an Ox Resin trainer.</p>
        </Card>
      </FullBleed>
    );
  }

  const isReturning = lookup.existing_account;
  const alreadyConfirmed = lookup.registration.confirmed;
  const sections = (lookup.course.sections || []).map(s => SECTION_LABEL[s] || s).join(' + ');
  const palette = paletteForCourse(lookup.course);

  return (
    <div className="min-h-screen py-8 px-4" style={{ background: `linear-gradient(180deg, ${palette.primary} 0%, ${palette.dark} 100%)` }}>
      <div className="max-w-md mx-auto">
        <button
          onClick={() => navigate(`/training/register/${courseId}?token=${encodeURIComponent(token)}`)}
          className="text-sm mb-3 inline-flex items-center gap-1 hover:opacity-100"
          style={{ color: palette.ink, opacity: 0.85 }}
          data-testid="training-confirm-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to register
        </button>

        <Card className="p-6 !bg-white" style={{ background: '#FFFFFF', borderTop: `4px solid ${palette.primary}` }}>
          <div className="text-center mb-5">
            <div className="w-14 h-14 mx-auto rounded-xl flex items-center justify-center mb-3" style={{ background: palette.tint, color: palette.dark }}>
              <GraduationCap className="w-7 h-7" />
            </div>
            <div className="text-xs uppercase tracking-wider text-gray-500">Confirming attendance</div>
            <h1 className="text-xl font-bold text-gray-900 mt-1">{lookup.course.name}</h1>
            <p className="text-sm text-gray-500">
              {lookup.course.date}
              {lookup.course.reference ? ` · ${lookup.course.reference}` : ''}
            </p>
          </div>

          {alreadyConfirmed && (
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800 mb-4" data-testid="training-already-confirmed">
              You&apos;ve already confirmed attendance for this course. Signing in below will refresh your session.
            </div>
          )}

          {/* Outstanding balance — informational only. Trainee pays Ox staff directly. */}
          {(lookup.registration.balance_owed || 0) > 0 && !lookup.registration.balance_paid_at && (
            <div className="p-3 rounded-lg bg-rose-50 border border-rose-200 text-sm text-rose-800 mb-4" data-testid="training-balance-notice">
              <div className="font-bold">Outstanding balance: £{Number(lookup.registration.balance_owed).toFixed(2)}</div>
              <div className="text-xs mt-0.5">Please settle this with your Ox Resin trainer before you leave.</div>
            </div>
          )}
          {(lookup.registration.balance_owed || 0) > 0 && lookup.registration.balance_paid_at && (
            <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-800 mb-4" data-testid="training-balance-paid">
              Balance of £{Number(lookup.registration.balance_owed).toFixed(2)} received — thank you.
            </div>
          )}

          <div className="space-y-3 mb-4">
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider">Name</div>
              <div className="font-semibold text-gray-900" data-testid="training-confirm-name">{lookup.registration.name}</div>
            </div>
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider">Email</div>
              <div className="font-semibold text-gray-900" data-testid="training-confirm-email">{lookup.registration.email}</div>
            </div>
            {lookup.registration.phone && (
              <div>
                <div className="text-xs text-gray-500 uppercase tracking-wider">Phone</div>
                <div className="font-semibold text-gray-900">{lookup.registration.phone}</div>
              </div>
            )}
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wider">You&apos;ll unlock</div>
              <div className="font-semibold" style={{ color: palette.dark }}>{sections || '—'}</div>
            </div>
          </div>

          <div className="mb-3">
            <Label htmlFor="tr-password" className="text-sm">
              {isReturning
                ? 'Enter your Ox Resin password to confirm'
                : 'Choose a password to create your Ox Resin account'}
            </Label>
            <Input
              id="tr-password"
              data-testid="training-confirm-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={isReturning ? 'Your existing password' : 'At least 6 characters'}
              minLength={6}
              autoFocus
              className="bg-white"
            />
            <p className="text-xs text-gray-500 mt-1">
              {isReturning
                ? 'We recognised your email — sign in with your existing password to confirm.'
                : 'We\u2019ll create your Ox Resin installer account with this password.'}
            </p>
          </div>

          <Button
            onClick={submit}
            disabled={busy || password.length < 6}
            data-testid="training-confirm-submit"
            className="w-full font-bold text-base py-6 hover:opacity-90"
            style={{ background: palette.primary, color: palette.ink }}
          >
            {busy
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Confirming…</>
              : (isReturning ? 'Confirm & sign in' : 'Confirm & create account')}
          </Button>
        </Card>
      </div>
    </div>
  );
}


function FullBleed({ children, palette }) {
  const p = palette || BRAND.green;
  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ background: `linear-gradient(180deg, ${p.primary} 0%, ${p.dark} 100%)` }}>
      {children}
    </div>
  );
}
